import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  RailwayDeploymentsResponse,
  RailwayLogGroup,
  RailwayLogsResponse,
  RailwayMetricsResponse,
} from '@internal/plugin-railway-common';

export class RailwayApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'RailwayApiError';
    this.status = status;
  }
}

export interface RailwayDeploymentsOptions {
  limit?: number;
  /** One of a previous `target.services`; the backend rejects anything else. */
  serviceId?: string;
}

export interface RailwayApi {
  getDeployments(
    entityRef: string,
    options?: RailwayDeploymentsOptions,
  ): Promise<RailwayDeploymentsResponse>;
  getLogs(
    entityRef: string,
    deploymentId: string,
    limit?: number,
  ): Promise<RailwayLogsResponse>;
  getMetrics(entityRef: string): Promise<RailwayMetricsResponse>;
}

export const railwayApiRef = createApiRef<RailwayApi>({
  id: 'plugin.railway.api',
});

/**
 * Reads `plugins/railway-backend`, which holds the Railway token.
 *
 * Every call names an entity rather than a project: the backend resolves the
 * annotations itself, so the browser never learns — or gets to choose — which
 * Railway project it is reading.
 */
export class RailwayClient implements RailwayApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async get<T>(
    path: string,
    params: Record<string, string>,
  ): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('railway');
    const search = new URLSearchParams(params).toString();
    const response = await this.fetchApi.fetch(`${base}${path}?${search}`);
    if (!response.ok) {
      // The router answers with `{ error }`; anything else is a proxy or a
      // crash, and the status alone is what there is to say.
      let message = `${response.status} ${response.statusText}`;
      try {
        const body = await response.json();
        if (typeof body?.error === 'string') message = body.error;
      } catch {
        /* keep the status line */
      }
      throw new RailwayApiError(message, response.status);
    }
    return response.json();
  }

  async getDeployments(
    entityRef: string,
    options: RailwayDeploymentsOptions = {},
  ): Promise<RailwayDeploymentsResponse> {
    const params: Record<string, string> = {
      entityRef,
      limit: String(options.limit ?? 10),
    };
    if (options.serviceId) params.serviceId = options.serviceId;
    return this.get('/deployments', params);
  }

  async getLogs(
    entityRef: string,
    deploymentId: string,
    limit = 200,
  ): Promise<RailwayLogsResponse> {
    const body = await this.get<{
      build?: Partial<RailwayLogGroup>;
      deploy?: Partial<RailwayLogGroup>;
    }>(`/deployments/${encodeURIComponent(deploymentId)}/logs`, {
      entityRef,
      limit: String(limit),
    });
    const group = (raw?: Partial<RailwayLogGroup>): RailwayLogGroup => ({
      lines: raw?.lines ?? [],
      hasMore: Boolean(raw?.hasMore),
    });
    return { build: group(body.build), deploy: group(body.deploy) };
  }

  /**
   * Project-wide, so it takes no service: every service is a line on the same
   * chart. The window is the backend's, not a parameter — see `/metrics`.
   */
  async getMetrics(entityRef: string): Promise<RailwayMetricsResponse> {
    const body = await this.get<Partial<RailwayMetricsResponse>>('/metrics', {
      entityRef,
    });
    return { charts: body.charts ?? [], error: body.error };
  }
}
